function initVideoSlider(root) {
  if (!root) return;
  const slider = root.querySelector('.slider-video');
  if (!slider) return;

  const videoItems = [...root.querySelectorAll('.video-item')];
  const videos = videoItems.map(item => item.querySelector('video')).filter(Boolean);
  const totalVideos = videoItems.length;

  const prevBtn = root.querySelector('.shop__video .prev');
  const nextBtn = root.querySelector('.shop__video .next');

  let currentIndex = 0;
  let sectionInViewport = false;

  const getColumns = () =>
    window.innerWidth <= 768 ? 1 : parseInt(slider.getAttribute('data-columns')) || 1;

  const getMaxIndex = () => Math.max(totalVideos - getColumns(), 0);

  const updateNavButtons = () => {
    const maxIndex = getMaxIndex();
    if (prevBtn) prevBtn.classList.toggle('disabled', currentIndex <= 0);
    if (nextBtn) nextBtn.classList.toggle('disabled', currentIndex >= maxIndex);
  };

  const updateSliderPosition = () => {
    const itemWidth = videoItems[0]?.offsetWidth || 0;
    slider.style.transform = `translateX(${-currentIndex * itemWidth}px)`;
    slider.style.transition = 'transform 0.4s ease-in-out';
    updateNavButtons();
  };

  const updateLayout = () => {
    const columns = getColumns();

    if (window.innerWidth <= 768) {
      videoItems.forEach(el => el.style.width = '100%');
    } else if (columns === 4 && totalVideos === 4) {
      videoItems.forEach(el => el.style.width = '25%');
    } else if (columns === 4 && totalVideos === 3) {
      videoItems.forEach(el => {
        el.style.width = '25%';
        el.style.flex = '0 0 auto';
      });
    } else {
      videoItems.forEach(el => el.style.width = `calc(100% / ${columns})`);
    }

    updateSliderPosition();
    updateNavButtons();
  };

  const pauseAllVideos = () => {
    videos.forEach(v => {
      if (!v.paused) v.pause();
      const mutedBtn = v.closest('.video_load')?.querySelector('.action_muted');
      if (mutedBtn) mutedBtn.classList.add('hidden');
    });
  };

  const playVideo = (index) => {
    const video = videos[index];
    if (!video || !sectionInViewport) return;

    videos.forEach((v, i) => {
      if (i !== index && !v.paused) {
        v.pause();
        const mutedBtn = v.closest('.video_load')?.querySelector('.action_muted');
        if (mutedBtn) mutedBtn.classList.add('hidden');
      }
    });

    if (video.paused) {
      video.play().catch(err => {
        if (err.name !== 'AbortError') console.error(err);
      });
    }

    const mutedBtn = video.closest('.video_load')?.querySelector('.action_muted');
    if (mutedBtn) mutedBtn.classList.remove('hidden');
  };

  // Viewport observer — only play videos when section is visible
  const sectionObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const wasInViewport = sectionInViewport;
      sectionInViewport = entry.isIntersecting;

      if (sectionInViewport && !wasInViewport) {
        playVideo(currentIndex);
      } else if (!sectionInViewport && wasInViewport) {
        pauseAllVideos();
      }
    });
  }, { threshold: 0.1, rootMargin: '100px 0px' });

  sectionObserver.observe(root);

  // Nav buttons
  nextBtn?.addEventListener('click', () => {
    if (currentIndex < getMaxIndex()) {
      currentIndex++;
      updateSliderPosition();
      playVideo(currentIndex);
    }
  });

  prevBtn?.addEventListener('click', () => {
    if (currentIndex > 0) {
      currentIndex--;
      updateSliderPosition();
      playVideo(currentIndex);
    }
  });

  // Video interaction handlers
  videos.forEach(video => {
    video.addEventListener('ended', () => {
      if (window.innerWidth <= 768 && currentIndex < totalVideos - 1) {
        currentIndex++;
        updateSliderPosition();
        playVideo(currentIndex);
      }
    });

    const playHandler = () => {
      if (!sectionInViewport) return;

      const targetIndex = videos.indexOf(video);
      if (targetIndex !== -1) playVideo(targetIndex);
    };

    video.addEventListener('mouseenter', playHandler);
    video.addEventListener('touchstart', playHandler, { passive: true });
    video.addEventListener('focusin', playHandler);
  });

  // Mute button handlers (using event delegation on the section)
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('.action_muted');
    if (!btn) return;

    const video = btn.closest('.video_load')?.querySelector('video');
    if (!video) return;

    video.muted = !video.muted;
    btn.classList.toggle('muted');
    btn.classList.toggle('unmuted');
  });

  // Keyboard support for mute button
  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const btn = e.target.closest('.action_muted');
    if (!btn) return;

    e.preventDefault();
    btn.click();
  });

  // Swipe support
  let touchStartX = 0;
  let touchStartY = 0;
  let isTouchingSlider = false;

  slider.addEventListener('touchstart', e => {
    isTouchingSlider = slider.contains(e.target);
    if (!isTouchingSlider) return;
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
  }, { passive: true });

  slider.addEventListener('touchend', e => {
    if (!isTouchingSlider) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    const dy = e.changedTouches[0].clientY - touchStartY;
    isTouchingSlider = false;

    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 100) {
      if (dx < 0 && currentIndex < getMaxIndex()) {
        currentIndex++;
      } else if (dx > 0 && currentIndex > 0) {
        currentIndex--;
      }
      updateSliderPosition();
      playVideo(currentIndex);
    }
  }, { passive: true });

  // Resize handler
  let resizeTimer;
  let lastIsWide = window.innerWidth > 750;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const isWide = window.innerWidth > 750;
      if (isWide !== lastIsWide) {
        updateLayout();
        lastIsWide = isWide;
      }
    }, 200);
  }, { passive: true });

  updateLayout();
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.section-shoppable-video').forEach(section => {
    initVideoSlider(section);
  });
});

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', e => {
    const section = document.getElementById(`shopify-section-${e.detail.sectionId}`);
    if (section) initVideoSlider(section);
  });
}